import React, { useEffect, useRef, useState } from "react";
import ExpensesListViewModel from "../../viewmodels/expenses/ExpensesListViewModel";
import UploadExpenseForm from "../../components/expenses/UploadExpenseForm";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import { useToast } from "../../contexts/ToastContext";

// Template Components
import DataManagementTemplate from "../../components/templates/DataManagementTemplate";
import MobileCardsTemplate from "../../components/templates/MobileCardsTemplate";

import EmptyModal from "../../components/modals/EmptyModal";
import ProgressModal from "../../components/modals/ProgressModal";

export default function ExpensesListView() {
  const { showSuccess } = useToast();
  
  const {
    loadExpenses,
    expenses,
    loading,
    uploadExpense,
    updateExpense,
    manualCreate,
    showFilters,
    setShowFilters,
    filters,
    resetFilters,
    filterComponents,
    deleteExpense,
  } = ExpensesListViewModel();

  const [currentPage, setCurrentPage] = useState(1);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");

  // load when page changes
  useEffect(() => {
    loadExpenses(currentPage, searchTerm, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // debounce search input (300ms)
  useEffect(() => {
    const t = setTimeout(() => {
      setCurrentPage(1);
      loadExpenses(1, searchTerm, filters);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters]);

  const searchRef = useRef();

  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const [progressModal, setProgressModal] = useState({
    open: false,
    childComponent: null,
  });

  // Search handler
  const handleSearch = (term) => {
    setSearchTerm(term);
    loadExpenses(currentPage, term || null, filters);
  };

  // Pagination handler
  const handlePageChange = (event) => {
    const newPage = event.page + 1;
    setCurrentPage(newPage);
    setFirst(event.first);
    setRows(event.rows);
    loadExpenses(newPage, searchTerm || null, filters);
  };

  async function handleUpload(formData) {
    setProgressModal({
      open: true,
      childComponent: <ProgressModal message="Uploading expense..." />,
    });
    try {
      const res = await uploadExpense(formData);
      if (res && res.success) {
      showSuccess(
        res.data.message || "Action completed"
      );
        setEmptyModelOpen({ open: false, childComponent: null });
        loadExpenses(currentPage, searchTerm, filters);
      } else {
        // Handle error case - don't show success message for failures
        console.error('Operation failed:', res?.data?.message);
      }
      return res;
    } finally {
      setProgressModal({ open: false, childComponent: null });
    }
  }

  async function handleManualCreate(payloadOrFormData) {
    setProgressModal({
      open: true,
      childComponent: <ProgressModal message="Creating expense..." />,
    });
    try {
      const res = await manualCreate(payloadOrFormData);
      if (res && res.success) {
        showSuccess(
          res.data.message || "Expense created successfully"
        );
        setEmptyModelOpen({ open: false, childComponent: null });
        loadExpenses(currentPage, searchTerm, filters);
      } else {
        // Handle error case - don't show success message for failures
        console.error('Operation failed:', res?.data?.message);
      }
      return res;
    } finally {
      setProgressModal({ open: false, childComponent: null });
    }
  }

  // Helper function to format date
  const formatDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Button configurations
  const headerButtons = [
    {
      label: "Manual Upload",
      onClick: () => setEmptyModelOpen({
        open: true,
        childComponent: (
          <UploadExpenseForm
            visible={true}
            onClose={() => setEmptyModelOpen({ open: false, childComponent: null })}
            onManualCreate={handleManualCreate}
            onUpdate={async (id, payload) => {
              const res = await updateExpense(id, payload);
              if (res && res.success) {
      showSuccess(
        res.data.message || "Action completed"
      );
                setEmptyModelOpen({ open: false, childComponent: null });
                loadExpenses(currentPage, searchTerm, filters);
      } else {
        // Handle error case - don't show success message for failures
        console.error('Operation failed:', res?.data?.message);
      }
              return res;
            }}
          />
        ),
      }),
      className: "bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white focus:ring-primary-500"
    },
    {
      label: "Automatic Upload",
      onClick: async () => {
        const AutomaticUploadModal = (await import("../../components/expenses/AutomaticUploadModal")).default;
        setEmptyModelOpen({
          open: true,
          childComponent: (
            <AutomaticUploadModal
              onCancel={() => setEmptyModelOpen({ open: false, childComponent: null })}
              onConfirm={async (formData) => {
                const res = await handleUpload(formData);
                if (res && res.success) {
                  setEmptyModelOpen({ open: false, childComponent: null });
                }
                return res;
              }}
            />
          ),
        });
      },
      className: "bg-white border border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300 focus:ring-primary-500"
    }
  ];

  // Filter configuration
  const filterConfig = {
    dropdown: true,
    components: filterComponents,
    onReset: resetFilters,
    onApply: () => {
      setCurrentPage(1);
      loadExpenses(1, searchTerm, filters);
    }
  };

  // Column configurations for DataTable
  const columns = [
    {
      field: "vendor",
      header: "Vendor",
      style: { width: '25%' },
      body: (rowData) => (
        <span className="font-semibold apollo-subheading text-gray-900">
          {rowData.vendor || "—"}
        </span>
      )
    },
    {
      field: "vendor_address",
      header: "Address",
      style: { width: '25%' },
      body: (rowData) => (
        <span className="apollo-body">
          {rowData.vendor_address || "—"}
        </span>
      )
    },
    {
      field: "date",
      header: "Date",
      style: { width: '20%' },
      body: (rowData) => (
        <span className="apollo-body">
          {formatDate(rowData.date)}
        </span>
      )
    },
    {
      field: "total_amount",
      header: "Total",
      style: { width: '15%' },
      body: (rowData) => (
        <span className="apollo-body font-medium">
          ${rowData.total_amount || "—"}
        </span>
      )
    },
    {
      header: "Actions",
      style: { width: '15%' },
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEmptyModelOpen({
                open: true,
                childComponent: (
                  <DeleteConfirmationForm
                    title="Delete expense"
                    message="Are you sure you want to delete this expense? This action cannot be undone."
                    buttonMessage="Delete"
                    setOpen={(o) => setEmptyModelOpen(o)}
                    confirmAction={async () => {
                      const res = await deleteExpense(rowData.id);
                      if (res && res.success) {
      showSuccess(
        res.data.message || "Action completed"
      );
                        setEmptyModelOpen({ open: false, childComponent: null });
                        loadExpenses(currentPage, searchTerm, filters);
      } else {
        // Handle error case - don't show success message for failures
        console.error('Operation failed:', res?.data?.message);
      }
                      return res;
                    }}
                  />
                ),
              });
            }}
            className="inline-flex items-center justify-center px-3 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 text-sm"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEmptyModelOpen({
                open: true,
                childComponent: (
                  <UploadExpenseForm
                    visible={true}
                    initialData={{
                      ...rowData,
                      receipt_url: rowData.receipt_path || rowData.receipt_url,
                    }}
                    onClose={() => setEmptyModelOpen({ open: false, childComponent: null })}
                    onUpdate={async (id, payload) => {
                      const res = await updateExpense(id, payload);
                      if (res && res.success) {
      showSuccess(
        res.data.message || "Action completed"
      );
                        setEmptyModelOpen({ open: false, childComponent: null });
                        loadExpenses(currentPage, searchTerm, filters);
      } else {
        // Handle error case - don't show success message for failures
        console.error('Operation failed:', res?.data?.message);
      }
                      return res;
                    }}
                  />
                ),
              });
            }}
            className="inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            View
          </button>
        </div>
      )
    }
  ];

  // Mobile cards component
  const ExpensesMobileCards = ({ data }) => (
    <MobileCardsTemplate
      data={data}
      onCardClick={(expense) => {
        setEmptyModelOpen({
          open: true,
          childComponent: (
            <UploadExpenseForm
              visible={true}
              initialData={{
                ...expense,
                receipt_url: expense.receipt_path || expense.receipt_url,
              }}
              onClose={() => setEmptyModelOpen({ open: false, childComponent: null })}
              onUpdate={async (id, payload) => {
                const res = await updateExpense(id, payload);
                if (res && res.success) {
      showSuccess(
        res.data.message || "Action completed"
      );
                  setEmptyModelOpen({ open: false, childComponent: null });
                  loadExpenses(currentPage, searchTerm, filters);
      } else {
        // Handle error case - don't show success message for failures
        console.error('Operation failed:', res?.data?.message);
      }
                return res;
              }}
            />
          ),
        });
      }}
      emptyMessage="No expenses found"
      cardContent={(expense) => (
        <div className="flex flex-col space-y-3">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-h6 apollo-heading text-primary-600 mb-1">
                {expense.vendor || "Unknown Vendor"}
              </h3>
              <p className="text-body-2 apollo-subheading">
                {formatDate(expense.date)}
              </p>
            </div>
            {expense.total_amount && (
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-base font-medium bg-green-100 text-green-800">
                ${expense.total_amount}
              </span>
            )}
          </div>

          {/* Details */}
          <div className="space-y-2">
            {expense.vendor_address && (
              <div className="flex justify-between text-body-2 apollo-body">
                <span>Address:</span>
                <span className="text-right">{expense.vendor_address}</span>
              </div>
            )}
          </div>
        </div>
      )}
      actionButtons={(expense) => (
        <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => {
              setEmptyModelOpen({
                open: true,
                childComponent: (
                  <UploadExpenseForm
                    visible={true}
                    initialData={{
                      ...expense,
                      receipt_url: expense.receipt_path || expense.receipt_url,
                    }}
                    onClose={() => setEmptyModelOpen({ open: false, childComponent: null })}
                    onUpdate={async (id, payload) => {
                      const res = await updateExpense(id, payload);
                      if (res && res.success) {
      showSuccess(
        res.data.message || "Action completed"
      );
                        setEmptyModelOpen({ open: false, childComponent: null });
                        loadExpenses(currentPage, searchTerm, filters);
      } else {
        // Handle error case - don't show success message for failures
        console.error('Operation failed:', res?.data?.message);
      }
                      return res;
                    }}
                  />
                ),
              });
            }}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            View
          </button>
          <button
            onClick={() => {
              setEmptyModelOpen({
                open: true,
                childComponent: (
                  <DeleteConfirmationForm
                    title="Delete expense"
                    message="Are you sure you want to delete this expense? This action cannot be undone."
                    buttonMessage="Delete"
                    setOpen={(o) => setEmptyModelOpen(o)}
                    confirmAction={async () => {
                      const res = await deleteExpense(expense.id);
                      if (res && res.success) {
      showSuccess(
        res.data.message || "Action completed"
      );
                        setEmptyModelOpen({ open: false, childComponent: null });
                        loadExpenses(currentPage, searchTerm, filters);
      } else {
        // Handle error case - don't show success message for failures
        console.error('Operation failed:', res?.data?.message);
      }
                      return res;
                    }}
                  />
                ),
              });
            }}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
        </div>
      )}
    />
  );

  return (
    <div>
      <DataManagementTemplate
        title="Expenses"
        description="Manage your expense records"
        headerButtons={headerButtons}
        filterConfig={filterConfig}
        columns={columns}
        data={expenses.data || []}
        loading={loading}
        pagination={{
          first: first,
          rows: rows,
          total: expenses.total || 0,
          template: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
          currentPageReportTemplate: "Showing {first} to {last} of {totalRecords} expenses"
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        onRowClick={(expense) => {
          setEmptyModelOpen({
            open: true,
            childComponent: (
              <UploadExpenseForm
                visible={true}
                initialData={{
                  ...expense,
                  receipt_url: expense.receipt_path || expense.receipt_url,
                }}
                onClose={() => setEmptyModelOpen({ open: false, childComponent: null })}
                onUpdate={async (id, payload) => {
                  const res = await updateExpense(id, payload);
                  if (res && res.success) {
      showSuccess(
        res.data.message || "Action completed"
      );
                    setEmptyModelOpen({ open: false, childComponent: null });
                    loadExpenses(currentPage, searchTerm, filters);
      } else {
        // Handle error case - don't show success message for failures
        console.error('Operation failed:', res?.data?.message);
      }
                  return res;
                }}
              />
            ),
          });
        }}
        searchPlaceholder="Search by vendor or notes"
        MobileCardsComponent={ExpensesMobileCards}
        emptyMessage="No expenses found"
      />
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={(o) => setEmptyModelOpen(o)}
      />
      <EmptyModal
        childComponent={progressModal.childComponent}
        open={progressModal.open}
        setOpen={setProgressModal}
      />
    </div>
  );
}
